import {
    useParams,
    useSearchParams,
} from 'react-router-dom';
import {Alert} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';

import {SimplePageLayout} from '@flexinet/ui-components';

import {IndexPageNavigation} from '../navigation/IndexPageNavigation';
import {RecordsSurface} from '../components/RecordsSurface';
import {useZoneNameQuery} from '../hooks/useDnsRecordsQuery';

// FNP-1192 — the records page.
//
// ⚠️ A RECORD HAS NO IDENTITY OUTSIDE ITS ZONE, which is why this is an index
// page reached from a zone and there is no detail page. The route is
// `/zones/:zoneId/records` (registered by FNP-1186) and the zone id comes from
// the path.
//
// FNP-1291 — the records themselves now live in `components/RecordsSurface`,
// which the zone detail page renders in its wide column. What is left here is
// the standalone page's own chrome: the layout, the breadcrumb back through the
// zone, and the guard for a link that arrived without a connection. The route
// stays deep-linkable and this page keeps working exactly as before.
export const IndexPage = () => {

    const {zoneId: zoneIdParam} = useParams();
    const [searchParams] = useSearchParams();

    if (typeof zoneIdParam === 'undefined') {
        throw new Error('zoneId is required parameter');
    }

    // The id arrives percent-encoded from the zone table's link. `DnsZone.id`
    // is opaque and the adapter is free to mint one containing characters that
    // are unsafe in a path segment, so decoding is what keeps the link and the
    // fetch in agreement rather than relying on today's shape.
    const zoneId = decodeURIComponent(zoneIdParam);

    // ⚠️ `listDnsRecords(connectionId: ID!, zoneId: ID!)` NEEDS BOTH, and the
    // route carries only the zone id. The connection rides in the query string
    // — every link this app renders to a zone carries one, which also makes the
    // URL shareable and reload-safe.
    //
    // Without it the page says so rather than firing a query guaranteed to fail
    // schema validation. The zones detail page resolves the same way and can
    // additionally fall back to its list cache; there is no equivalent cache
    // here, because this module never lists zones.
    const connectionId = searchParams.get('connectionId') ?? undefined;

    // ⚠️ THE GUARD COMES BEFORE ANY QUERY HOOK, and the ordering is
    // load-bearing rather than stylistic.
    //
    // With no connection there is nothing to fetch — both reads require it —
    // so calling a query hook first would mount React Query machinery purely to
    // render a message that needs none of it. It also makes the page usable in
    // a context that has no QueryClientProvider (the routing tests are exactly
    // that), where an early hook throws before the guard can explain itself.
    if (!connectionId) {
        return (
            <Alert color={'yellow'} title={I18n.get('Cannot open these records directly')}>
                {I18n.get('A zone\'s records can only be read through the connection the zone belongs to, and this link does not name one. Open the zone from the Zones list, then its records.')}
            </Alert>
        );
    }

    return (
        <RecordsPage connectionId={connectionId} zoneId={zoneId}/>
    );
};

// The page proper, mounted only once a connection is resolved — so every hook
// below can rely on having one rather than guarding individually.
const RecordsPage = (
    {
        connectionId,
        zoneId,
    }: {
        connectionId: string,
        zoneId: string,
    },
) => {

    // ⚠️ READ HERE *AND* INSIDE THE SURFACE, and that is not a duplicate fetch:
    // both calls are the same React Query key, so the second is served from
    // cache. The breadcrumb is this page's chrome and the surface is shared, so
    // each reads the name it needs rather than one prop-drilling through the
    // other.
    const {data: zoneName} = useZoneNameQuery(connectionId, zoneId);

    return (
        <SimplePageLayout
            pageNavigation={
                <IndexPageNavigation
                    zoneId={zoneId}
                    connectionId={connectionId}
                    zoneName={zoneName ?? undefined}
                />
            }
        >
            <RecordsSurface
                connectionId={connectionId}
                zoneId={zoneId}
            />
        </SimplePageLayout>
    );
};
